import { useState, useMemo } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ── Palette YOMA ──────────────────────────────────────────────────────────────
const GOLD   = "#C9A96E";
const INK    = "#1C1915";
const WARM   = "#F8F4EE";
const BORDER = "#E7E1D8";
const MUTED  = "#8B7361";
const WHITE  = "#FFFFFF";
const SOFT   = "#F2EDE6";
const RED    = "#C0392B";

// ── Montant en lettres (français) ─────────────────────────────────────────────
const ONES = ["","un","deux","trois","quatre","cinq","six","sept","huit","neuf","dix","onze","douze","treize","quatorze","quinze","seize","dix-sept","dix-huit","dix-neuf"];
const TENS = ["","","vingt","trente","quarante","cinquante","soixante","soixante","quatre-vingt","quatre-vingt"];

function below100(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10), u = n % 10;
  if (t === 7) return "soixante-" + (u === 1 ? "et-onze" : ONES[10 + u]);
  if (t === 9) return "quatre-vingt-" + (u === 0 ? "dix" : ONES[10 + u]);
  if (t === 8) return "quatre-vingt" + (u === 0 ? "s" : "-" + ONES[u]);
  return TENS[t] + (u === 0 ? "" : (u === 1 ? "-et-" : "-") + ONES[u]);
}
function below1000(n) {
  if (n === 0) return "";
  if (n < 100) return below100(n);
  const h = Math.floor(n / 100), r = n % 100;
  return (h === 1 ? "cent" : ONES[h] + " cent") + (r === 0 ? (h > 1 ? "s" : "") : " " + below100(r));
}
function numberToFrench(n) {
  n = Math.round(n); if (n === 0) return "zéro";
  let r = "";
  if (n >= 1000000) { const m = Math.floor(n / 1000000); r += m === 1 ? "un million" : below1000(m) + " millions"; n %= 1000000; if (n) r += " "; }
  if (n >= 1000)    { const k = Math.floor(n / 1000);    r += k === 1 ? "mille" : below1000(k) + " mille"; n %= 1000; if (n) r += " "; }
  if (n > 0) r += below1000(n);
  return r;
}

const DEVISE_LABELS  = { MAD: "dirhams marocains", EUR: "euros", USD: "dollars américains", GBP: "livres sterling", CHF: "francs suisses" };
const DEVISE_SYMBOLS = { MAD: "MAD", EUR: "€", USD: "$", GBP: "£", CHF: "CHF" };

function fmtM(n)              { return Math.round(n).toLocaleString("fr-FR"); }
function enLettres(n, devise) { return numberToFrench(n) + " " + (DEVISE_LABELS[devise] || devise) + " toutes taxes comprises"; }
function fmtDate(d)           { return d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "___________"; }

// ── Données initiales ─────────────────────────────────────────────────────────
const INIT_PROJECTS = [
  { id: 1, nom: "PROJET CACI – CHARLOTTE", client: "Madame Charlotte CACI",
    lot: "Lot 156 — Tranche 10", tf: "TF 36565/65",
    surfaceTerrain: "383", surfaceVilla: "215", ville: "Marrakech",
    montantTTC: 110000, devise: "EUR", duree: "12 à 15 mois maximum" },
];
const INIT_COMPANIES = [
  { id: 1, nom: "CM KABDANI CONSTRUCTION SARLAU", rc: "166873",
    ice: "003751270000006", siege: "Douar Chouiter Alouidane – Marrakech",
    representant: "KABDANI MUSTAPHA", cin: "F824320" },
];

const blankProj = () => ({ id: Date.now(), nom: "", client: "", lot: "", tf: "", surfaceTerrain: "", surfaceVilla: "", ville: "Marrakech", montantTTC: "", devise: "MAD", duree: "12 à 15 mois maximum" });
const blankComp = () => ({ id: Date.now(), nom: "", rc: "", ice: "", siege: "", representant: "", cin: "" });

// ── Génération PDF ─────────────────────────────────────────────────────────────
function generatePDF({ projet: p, entreprise: e, dateSignature, dateDemarrage }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297, mL = 20, mR = 20, mT = 28, mB = 22, cW = 170;
  let y = mT, pageNum = 1;
  const todayStr = dateSignature || new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

  // helpers
  const hdr = () => {
    doc.setFillColor(28, 25, 21); doc.rect(0, 0, W, 16, "F");
    doc.setDrawColor(201, 169, 110); doc.setLineWidth(0.3); doc.line(0, 16, W, 16);
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(201, 169, 110);
    doc.text("YOMA DESIGN · ARCHITECTURE INTÉRIEURE & COORDINATION", W / 2, 8, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(100, 80, 60);
    doc.text(`CONTRAT D'EXÉCUTION DES TRAVAUX · ${p.nom}`, W / 2, 13.5, { align: "center" });
  };
  const ftr = (pn) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(150, 130, 110);
    doc.text("YOMA DESIGN · Document confidentiel", mL, H - 8);
    doc.text(todayStr, W / 2, H - 8, { align: "center" });
    doc.text(`Page ${pn}`, W - mR, H - 8, { align: "right" });
  };
  const np = () => { ftr(pageNum); doc.addPage(); pageNum++; hdr(); y = mT + 7; };
  const chk = (n = 12) => { if (y + n > H - mB) np(); };

  const title = (txt) => {
    chk(14); doc.setFillColor(28, 25, 21); doc.rect(mL, y - 2, cW, 9, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(201, 169, 110);
    doc.text(txt, mL + 4, y + 5); y += 12; doc.setTextColor(35, 30, 25);
  };
  const body = (txt, ind = 0, rgb = [35, 30, 25]) => {
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...rgb);
    const ls = doc.splitTextToSize(txt, cW - ind);
    chk(ls.length * 4.8 + 2); doc.text(ls, mL + ind, y); y += ls.length * 4.8 + 2;
  };
  const bullet = (txt) => {
    const ls = doc.splitTextToSize(txt, cW - 8);
    chk(ls.length * 4.8 + 1.5);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(201, 169, 110);
    doc.text("•", mL + 2, y);
    doc.setTextColor(35, 30, 25); doc.setFont("helvetica", "normal");
    doc.text(ls, mL + 8, y); y += ls.length * 4.8 + 1.5;
  };
  const num = (n, txt) => {
    const ls = doc.splitTextToSize(txt, cW - 10);
    chk(ls.length * 4.8 + 1.5);
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(35, 30, 25);
    doc.text(`${n}.`, mL + 2, y);
    doc.setFont("helvetica", "normal"); doc.text(ls, mL + 10, y);
    y += ls.length * 4.8 + 1.5;
  };
  const sp = (h = 3) => { y += h; };
  const hr = () => { sp(2); doc.setDrawColor(220, 210, 195); doc.setLineWidth(0.2); doc.line(mL, y, mL + cW, y); sp(4); };

  // ── PAGE DE GARDE ──────────────────────────────────────────────────────────
  doc.setFillColor(28, 25, 21); doc.rect(0, 0, W, H, "F");
  doc.setDrawColor(201, 169, 110); doc.setLineWidth(0.5);
  doc.line(mL, 50, W - mR, 50); doc.line(mL, H - 50, W - mR, H - 50);

  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(201, 169, 110);
  doc.text("YOMA DESIGN", W / 2, 39, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(120, 100, 75);
  doc.text("ARCHITECTURE INTÉRIEURE · MANDATAIRE DE MAÎTRISE D'OUVRAGE", W / 2, 47, { align: "center" });

  doc.setFont("helvetica", "bold"); doc.setFontSize(30); doc.setTextColor(255, 255, 255);
  doc.text("CONTRAT", W / 2, 97, { align: "center" });
  doc.setFontSize(14); doc.setTextColor(201, 169, 110);
  doc.text("D'EXÉCUTION DES TRAVAUX", W / 2, 109, { align: "center" });
  doc.setFontSize(13); doc.setTextColor(255, 255, 255);
  doc.text(p.nom, W / 2, 128, { align: "center" });

  // Encadré parties
  doc.setFillColor(38, 34, 30); doc.roundedRect(22, H - 118, W - 44, 67, 3, 3, "F");
  doc.setDrawColor(201, 169, 110); doc.setLineWidth(0.3); doc.line(W / 2, H - 113, W / 2, H - 57);
  const cx1 = 22 + (W / 2 - 22) / 2, cx2 = W / 2 + (W - mR - W / 2) / 2;
  // MO
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(201, 169, 110);
  doc.text("MAÎTRE D'OUVRAGE", cx1, H - 107, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  doc.text(p.client, cx1, H - 99, { align: "center" });
  doc.setFontSize(7.5); doc.setTextColor(140, 120, 95);
  doc.text(p.lot, cx1, H - 91, { align: "center" });
  doc.text(`${p.tf} · Terrain ${p.surfaceTerrain} m²`, cx1, H - 84, { align: "center" });
  // Entreprise
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(201, 169, 110);
  doc.text("ENTREPRISE", cx2, H - 107, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  doc.text(e.nom, cx2, H - 99, { align: "center" });
  doc.setFontSize(7.5); doc.setTextColor(140, 120, 95);
  doc.text(`RC n° ${e.rc} · ICE ${e.ice}`, cx2, H - 91, { align: "center" });
  doc.text(e.representant, cx2, H - 84, { align: "center" });
  // Date
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(80, 70, 60);
  doc.text(`Marrakech, le ${todayStr}`, W / 2, H - 41, { align: "center" });
  if (dateDemarrage) doc.text(`Date de démarrage prévue : ${dateDemarrage}`, W / 2, H - 34, { align: "center" });

  // ── PAGE 2 — CORPS ─────────────────────────────────────────────────────────
  doc.addPage(); pageNum = 2; hdr(); y = mT + 7;

  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(35, 30, 25);
  doc.text("CONTRAT D'EXÉCUTION DES TRAVAUX", W / 2, y, { align: "center" }); y += 5;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(201, 169, 110);
  doc.text(p.nom, W / 2, y, { align: "center" }); y += 8;

  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(35, 30, 25);
  doc.text("ENTRE LES SOUSSIGNÉS :", mL, y); y += 7;
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.text("YOMA DESIGN", mL, y); y += 5;
  body("Représentée par Monsieur Youssef Kabdani"); body("Agissant exclusivement en qualité de mandataire du Maître d'Ouvrage");
  body("Ci-après désignée : « YOMA DESIGN »", 0, [100, 85, 65]); sp(3);
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(35, 30, 25); doc.text(e.nom, mL, y); y += 5;
  body(`RC n° ${e.rc} · ICE : ${e.ice}`); body(`Siège : ${e.siege}`);
  body(`Représentée par ${e.representant}${e.cin ? " · CIN " + e.cin : ""}`);
  body("Ci-après désignée : « L'Entreprise »", 0, [100, 85, 65]); sp(3);
  body("Il est convenu ce qui suit :"); hr();

  // Art 1
  title("ARTICLE 1 — OBJET");
  body(`Le présent contrat a pour objet l'exécution par l'Entreprise des travaux relatifs au projet immobilier du Maître d'Ouvrage, ${p.client}, situé à ${p.lot}.`); sp(2);
  body(`Titre Foncier : ${p.tf} · Superficie terrain : ${p.surfaceTerrain} m² · Superficie villa : environ ${p.surfaceVilla} m², tel que décrit en Annexe A (Descriptif technique et cahier des charges).`); sp(2);
  body("YOMA DESIGN intervient exclusivement en qualité de mandataire du Maître d'Ouvrage pour la coordination administrative et la gestion financière du projet."); sp(3);

  // Art 2
  title("ARTICLE 2 — QUALIFICATION DES PARTIES");
  body("L'Entreprise reconnaît expressément que :");
  bullet("YOMA DESIGN agit au nom et pour le compte du Maître d'Ouvrage ;");
  bullet("YOMA DESIGN n'est ni maître d'ouvrage, ni entrepreneur général ;");
  bullet("YOMA DESIGN n'exécute aucun travail matériel ;");
  bullet("YOMA DESIGN n'assume aucune responsabilité technique d'exécution.");
  sp(2); body("L'Entreprise contracte en toute indépendance juridique, technique, économique et fiscale, et supporte seule l'ensemble des risques liés à son activité d'exécution."); sp(3);

  // Art 3
  title("ARTICLE 3 — RESPONSABILITÉ TECHNIQUE EXCLUSIVE");
  body("L'Entreprise est seule responsable :");
  bullet("De l'exécution matérielle des travaux et de leur conformité aux normes marocaines en vigueur ;");
  bullet("De la qualité des ouvrages réalisés et des malfaçons ;");
  bullet("De la sécurité du chantier, de ses salariés et sous-traitants ;");
  bullet("Des garanties légales applicables selon la réglementation en vigueur.");
  sp(2); body("Aucune solidarité contractuelle, technique ou financière ne pourra être recherchée à l'encontre de YOMA DESIGN."); sp(3);

  // Art 4
  title("ARTICLE 4 — ASSURANCES");
  body("L'Entreprise s'engage à fournir avant tout démarrage une attestation d'assurance Responsabilité Civile professionnelle valide. Lorsque la nature des travaux l'exige, l'Entreprise souscrira toute assurance spécifique requise, notamment une assurance décennale le cas échéant."); sp(3);

  // Art 5 — MONTANT
  title("ARTICLE 5 — MONTANT DU MARCHÉ / MODALITÉS DE PAIEMENT");
  chk(22);
  doc.setFillColor(248, 244, 238); doc.roundedRect(mL, y, cW, 20, 2, 2, "F");
  doc.setDrawColor(201, 169, 110); doc.setLineWidth(0.4); doc.roundedRect(mL, y, cW, 20, 2, 2, "S");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(201, 169, 110);
  doc.text("MONTANT GLOBAL ET FORFAITAIRE", W / 2, y + 6, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(28, 25, 21);
  doc.text(`${fmtM(p.montantTTC)} ${DEVISE_SYMBOLS[p.devise] || p.devise} TTC`, W / 2, y + 15, { align: "center" }); y += 24;
  sp(2);
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(80, 70, 60);
  const ll = doc.splitTextToSize(`(${enLettres(p.montantTTC, p.devise)})`, cW);
  chk(ll.length * 4.8 + 4); doc.text(ll, W / 2, y, { align: "center" }); y += ll.length * 4.8 + 4;
  if (p.devise === "EUR") { body("Soit l'équivalent en dirhams marocains au taux de change applicable au jour du paiement."); sp(2); }
  body("Le présent contrat constitue un marché forfaitaire global et définitif. Les paiements seront effectués par YOMA DESIGN en qualité de mandataire du Maître d'Ouvrage, après validation écrite d'un constat d'avancement réel et sur présentation de justificatifs documentés."); sp(2);
  body("Échéancier de paiement :"); sp(1);
  num(1, `30 % au démarrage effectif — ${fmtM(p.montantTTC * 0.30)} ${DEVISE_SYMBOLS[p.devise]}`);
  num(2, `20 % à l'achèvement des fondations et première dalle — ${fmtM(p.montantTTC * 0.20)} ${DEVISE_SYMBOLS[p.devise]}`);
  num(3, `20 % à l'achèvement complet du gros œuvre — ${fmtM(p.montantTTC * 0.20)} ${DEVISE_SYMBOLS[p.devise]}`);
  num(4, `20 % en cours de second œuvre — ${fmtM(p.montantTTC * 0.20)} ${DEVISE_SYMBOLS[p.devise]}`);
  num(5, `10 % à la réception finale — ${fmtM(p.montantTTC * 0.10)} ${DEVISE_SYMBOLS[p.devise]}`);
  sp(2); body("Aucun paiement n'est exigible sans validation formelle de YOMA DESIGN."); sp(3);

  // Art 6
  title("ARTICLE 6 — INTERDICTION DE CONTACT DIRECT");
  bullet("De solliciter directement le Maître d'Ouvrage pour tout paiement ;");
  bullet("De négocier directement toute modification financière ;");
  bullet("De contourner YOMA DESIGN pour toute demande contractuelle.");
  sp(2); body("Tout contournement constitue une faute contractuelle grave pouvant entraîner résiliation immédiate."); sp(3);

  // Art 7
  title("ARTICLE 7 — RESPECT DU PLANNING");
  body("L'Entreprise s'engage à respecter le planning annexé. Tout retard injustifié devra être signalé immédiatement à YOMA DESIGN. En cas de retard grave ou répété, une mise en demeure pourra être adressée."); sp(2);
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(35, 30, 25);
  doc.text(`Durée contractuelle : ${p.duree}`, mL, y); y += 5;
  if (dateDemarrage) { doc.text(`Date de démarrage prévue : ${dateDemarrage}`, mL, y); y += 5; }
  else { doc.setFont("helvetica", "normal"); body("La date de démarrage sera fixée par ordre de service écrit de YOMA DESIGN."); }
  doc.setFont("helvetica", "normal"); sp(3);

  // Art 8
  title("ARTICLE 8 — PROCÉDURE DE SUBSTITUTION");
  body("En cas d'abandon de chantier, de retard grave injustifié, de non-conformité persistante ou de manquement contractuel majeur, YOMA DESIGN pourra :");
  num(1, "Adresser une mise en demeure écrite avec délai de régularisation de 7 jours ;");
  num(2, "Résilier le contrat en cas de non-régularisation ;");
  num(3, "Organiser le remplacement de l'Entreprise sans indemnité pour l'Entreprise défaillante."); sp(3);

  // Art 9
  title("ARTICLE 9 — NON-SOLIDARITÉ");
  body("Aucune solidarité juridique n'existe entre YOMA DESIGN et l'Entreprise. L'Entreprise ne pourra engager la responsabilité de YOMA DESIGN pour tout acte relevant de sa mission d'exécution technique."); sp(3);

  // Art 10 — VERROUILLÉ
  title("ARTICLE 10 — CONFIDENTIALITÉ");
  body("L'Entreprise s'engage à ne divulguer aucune information relative :");
  bullet("Au montant global du projet ;");
  bullet("Aux conditions contractuelles entre YOMA DESIGN et le Maître d'Ouvrage ;");
  bullet("À l'organisation financière interne et à la structure de coordination de YOMA DESIGN.");
  sp(2); body("Cette obligation de confidentialité est perpétuelle et survit à l'extinction du contrat."); sp(3);

  // Art 11
  title("ARTICLE 11 — RÉSILIATION");
  body("En cas de manquement grave, le contrat pourra être résilié après mise en demeure restée sans effet. Les travaux réalisés et conformes seront réglés au prorata de leur avancement validé."); sp(3);

  // Art 12 — VERROUILLÉ ARBITRAGE
  title("ARTICLE 12 — DROIT APPLICABLE ET RÈGLEMENT DES LITIGES");
  body("Le présent contrat est soumis au droit marocain. Tout différend fera l'objet d'une tentative de résolution amiable dans un délai de 15 jours. À défaut, il sera tranché par voie d'arbitrage conformément à la loi marocaine n° 95-17 relative à l'arbitrage et à la médiation conventionnelle."); sp(2);
  chk(18);
  doc.setFillColor(28, 25, 21); doc.roundedRect(mL, y, cW, 15, 2, 2, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(201, 169, 110);
  doc.text("ARBITRE UNIQUE DÉSIGNÉ D'UN COMMUN ACCORD", W / 2, y + 5.5, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  doc.text("Monsieur Abderrahim ABOUELOUAFA · Marrakech · Langue : français", W / 2, y + 11, { align: "center" });
  y += 18; sp(3);

  // Art 13
  title("ARTICLE 13 — RÉCEPTION DES TRAVAUX");
  body("La réception interviendra en présence du Maître d'Ouvrage, de YOMA DESIGN et de l'Entreprise. Un procès-verbal de réception sera établi. La réception déclenche la garantie de parfait achèvement."); sp(8);

  // Signatures
  chk(55);
  doc.setDrawColor(201, 169, 110); doc.setLineWidth(0.4); doc.line(mL, y, mL + cW, y); y += 6;
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(35, 30, 25);
  doc.text("Fait en deux (2) exemplaires originaux", W / 2, y, { align: "center" }); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(100, 85, 70);
  doc.text(`À Marrakech, le ${todayStr}`, W / 2, y, { align: "center" }); y += 12;

  const s1x = mL + 6, s2x = W - mR - 76, sW = 70;
  // Entreprise
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(35, 30, 25);
  doc.text(e.nom, s1x + sW / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(100, 85, 70);
  doc.text(e.representant, s1x + sW / 2, y + 5, { align: "center" });
  doc.text("Cachet + Signature + « Bon pour accord »", s1x + sW / 2, y + 10, { align: "center" });
  doc.setDrawColor(180, 160, 130); doc.setLineWidth(0.3); doc.rect(s1x, y + 13, sW, 22);
  // YOMA
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(201, 169, 110);
  doc.text("YOMA DESIGN", s2x + sW / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(100, 85, 70);
  doc.text("Youssef Kabdani · Mandataire", s2x + sW / 2, y + 5, { align: "center" });
  doc.text("Cachet et Signature YOMA DESIGN", s2x + sW / 2, y + 10, { align: "center" });
  doc.rect(s2x, y + 13, sW, 22);

  ftr(pageNum);
  doc.save(`YOMA_Contrat_${p.nom.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════════════
export default function ContractAIGeneratorPage() {
  const [projects,     setProjects]     = useState(INIT_PROJECTS);
  const [companies,    setCompanies]    = useState(INIT_COMPANIES);
  const [projId,       setProjId]       = useState(INIT_PROJECTS[0].id);
  const [compId,       setCompId]       = useState(INIT_COMPANIES[0].id);
  const [dateSign,     setDateSign]     = useState("");
  const [dateStart,    setDateStart]    = useState("");
  const [montantOvr,   setMontantOvr]   = useState("");
  const [showProjMod,  setShowProjMod]  = useState(false);
  const [showCompMod,  setShowCompMod]  = useState(false);
  const [newProj,      setNewProj]      = useState(blankProj());
  const [newComp,      setNewComp]      = useState(blankComp());

  const projet     = projects.find(p => p.id === projId)  || projects[0];
  const entreprise = companies.find(c => c.id === compId) || companies[0];
  const montantEff = montantOvr ? parseFloat(montantOvr) : (projet?.montantTTC || 0);
  const montantAlert = montantOvr && parseFloat(montantOvr) !== projet?.montantTTC;
  const effectiveProjet = { ...projet, montantTTC: montantEff };

  const lettres = useMemo(() => enLettres(montantEff, projet?.devise || "MAD"), [montantEff, projet?.devise]);

  const exportPDF = () => generatePDF({
    projet: effectiveProjet, entreprise,
    dateSignature: fmtDate(dateSign),
    dateDemarrage: dateStart ? fmtDate(dateStart) : "",
  });

  const saveProj = () => { if (!newProj.nom || !newProj.client) return; setProjects(p => [...p, { ...newProj, id: Date.now() }]); setNewProj(blankProj()); setShowProjMod(false); };
  const saveComp = () => { if (!newComp.nom) return; setCompanies(c => [...c, { ...newComp, id: Date.now() }]); setNewComp(blankComp()); setShowCompMod(false); };

  // styles
  const cinp = { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "7px 10px", fontSize: 12, color: INK, fontFamily: "'DM Sans',sans-serif", width: "100%", boxSizing: "border-box" };
  const lbl  = { color: MUTED, fontSize: 10, fontWeight: 700, display: "block", marginBottom: 3, letterSpacing: "0.05em" };
  const dark = { ...cinp, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", color: WHITE };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#ECEAE6", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:wght@600;700&display=swap');
        .cbtn { border:none; border-radius:7px; padding:9px 14px; cursor:pointer; font-size:12px; font-weight:600; font-family:'DM Sans',sans-serif; transition:all .15s; }
        .cbtn:hover { opacity:.85; transform:translateY(-1px); }
        .csel { background:${WHITE}; border:1px solid ${BORDER}; border-radius:6px; padding:7px 10px; font-size:12px; color:${INK}; width:100%; box-sizing:border-box; }
        .cinp { background:${WHITE}; border:1px solid ${BORDER}; border-radius:6px; padding:7px 10px; font-size:12px; color:${INK}; font-family:'DM Sans',sans-serif; width:100%; box-sizing:border-box; }
        .cinp:focus,.csel:focus { outline:none; border-color:${GOLD}; }
        .art  { margin-bottom:16px; }
        .atit { background:${INK}; color:${GOLD}; font-weight:700; font-size:11px; letter-spacing:.08em; padding:5px 10px; border-radius:4px; margin-bottom:6px; display:flex; align-items:center; gap:8px; }
        .abod { font-size:12px; line-height:1.8; color:#2C2420; padding:0 4px; }
        .blt  { display:flex; gap:8px; margin:3px 0; }
        .blt-d{ color:${GOLD}; font-weight:700; flex-shrink:0; }
        .lock { font-size:9px; background:rgba(201,169,110,0.15); color:${GOLD}; border:1px solid rgba(201,169,110,0.3); border-radius:3px; padding:2px 6px; }
        .mbox { background:${SOFT}; border:1px solid ${BORDER}; border-radius:10px; text-align:center; padding:16px; margin:10px 0; }
        .abox { background:${INK}; border-radius:8px; padding:12px 16px; color:${WHITE}; text-align:center; margin:8px 0; }
        .sig  { border:1px solid ${BORDER}; border-radius:8px; padding:14px; background:${SOFT}; }
        .ech-row { display:flex; justify-content:space-between; border-bottom:1px solid ${BORDER}; padding:5px 0; font-size:12px; }
        .mini-info { background:rgba(255,255,255,.04); border-radius:6px; padding:8px 10px; font-size:11px; margin-top:6px; }
      `}</style>

      {/* ════════ SIDEBAR ════════ */}
      <aside style={{ width: 256, background: INK, flexShrink: 0, display: "flex", flexDirection: "column", padding: "18px 14px", overflowY: "auto" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 22, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ color: GOLD, fontFamily: "'Playfair Display',serif", fontSize: 17, fontWeight: 700, letterSpacing: 3 }}>YOMA</div>
          <div style={{ color: "#6A5D52", fontSize: 9, letterSpacing: "0.18em", marginTop: 3 }}>GÉNÉRATEUR DE CONTRAT</div>
        </div>

        {/* Projet */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em" }}>PROJET</span>
            <button className="cbtn" onClick={() => { setNewProj(blankProj()); setShowProjMod(true); }}
              style={{ background: "rgba(201,169,110,.18)", color: GOLD, padding: "3px 9px", fontSize: 11 }}>+ Nouveau</button>
          </div>
          <select value={projId} onChange={e => { setProjId(Number(e.target.value)); setMontantOvr(""); }} style={dark}>
            {projects.map(p => <option key={p.id} value={p.id} style={{ background: INK }}>{p.nom}</option>)}
          </select>
          {projet && (
            <div className="mini-info">
              <div style={{ color: "#8A7B6D" }}>{projet.client}</div>
              <div style={{ color: "#6A5D52" }}>{projet.lot}</div>
              <div style={{ color: "#6A5D52" }}>{projet.tf} · Terrain {projet.surfaceTerrain} m² · Villa {projet.surfaceVilla} m²</div>
            </div>
          )}
        </div>

        {/* Entreprise */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em" }}>ENTREPRISE</span>
            <button className="cbtn" onClick={() => { setNewComp(blankComp()); setShowCompMod(true); }}
              style={{ background: "rgba(201,169,110,.18)", color: GOLD, padding: "3px 9px", fontSize: 11 }}>+ Nouvelle</button>
          </div>
          <select value={compId} onChange={e => setCompId(Number(e.target.value))} style={dark}>
            {companies.map(c => <option key={c.id} value={c.id} style={{ background: INK }}>{c.nom}</option>)}
          </select>
          {entreprise && (
            <div className="mini-info">
              <div style={{ color: "#8A7B6D" }}>RC {entreprise.rc} · ICE {entreprise.ice}</div>
              <div style={{ color: "#6A5D52" }}>{entreprise.representant}</div>
              <div style={{ color: "#6A5D52" }}>{entreprise.siege}</div>
            </div>
          )}
        </div>

        <div style={{ height: 1, background: "rgba(255,255,255,.06)", margin: "2px 0 14px" }} />

        {/* Dates */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 8 }}>DATES</div>
          <label style={{ ...lbl, color: "#6A5D52" }}>Date de signature</label>
          <input type="date" value={dateSign} onChange={e => setDateSign(e.target.value)} style={{ ...dark, marginBottom: 8 }} />
          <label style={{ ...lbl, color: "#6A5D52" }}>Date de démarrage prévue</label>
          <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} style={dark} />
        </div>

        {/* Montant */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 6 }}>MONTANT — ARTICLE 5</div>
          <div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>
            {fmtM(projet?.montantTTC || 0)} <span style={{ fontSize: 12, color: "#8A7B6D" }}>{DEVISE_SYMBOLS[projet?.devise]} TTC</span>
          </div>
          <div style={{ color: "#5A4E46", fontSize: 10, fontStyle: "italic", marginBottom: 6 }}>Source : module Estimatif travaux</div>
          {montantAlert && (
            <div style={{ background: "rgba(192,57,43,.15)", border: "1px solid rgba(192,57,43,.4)", borderRadius: 6, padding: "5px 8px", marginBottom: 6, fontSize: 10, color: "#E87070" }}>
              ⚠️ Montant modifié — vérifiez avec l'Estimatif avant export
            </div>
          )}
          <label style={{ ...lbl, color: "#6A5D52" }}>Modifier le montant TTC</label>
          <input type="number" placeholder={`${projet?.montantTTC || 0}`} value={montantOvr}
            onChange={e => setMontantOvr(e.target.value)} style={dark} />
        </div>

        <div style={{ flex: 1 }} />

        <button className="cbtn" onClick={exportPDF}
          style={{ background: GOLD, color: INK, width: "100%", padding: "12px 0", fontSize: 14, fontWeight: 700, marginTop: 8 }}>
          ⬇ Exporter en PDF
        </button>
        <div style={{ color: "#5A4E46", fontSize: 10, textAlign: "center", marginTop: 5 }}>
          Contrat 13 articles · Prêt pour signature
        </div>
      </aside>

      {/* ════════ PREVIEW ════════ */}
      <main style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>
        {/* Title bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display',serif", color: INK }}>Contrat d'Exécution des Travaux</div>
            <div style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>Prévisualisation dynamique · YOMA DESIGN</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: "rgba(39,174,96,.1)", color: "#27AE60", border: "1px solid rgba(39,174,96,.3)", borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>✓ 13 Articles</span>
            <span style={{ background: "rgba(201,169,110,.1)", color: GOLD, border: `1px solid rgba(201,169,110,.3)`, borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>🔒 Clauses verrouillées</span>
          </div>
        </div>

        {/* Alerte montant */}
        {montantAlert && (
          <div style={{ background: "rgba(192,57,43,.08)", border: "1px solid rgba(192,57,43,.3)", borderRadius: 10, padding: "10px 16px", marginBottom: 18, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: RED, fontSize: 13 }}>Montant de l'Article 5 modifié manuellement</div>
              <div style={{ color: "#8B3030", fontSize: 12, marginTop: 2 }}>
                Montant saisi : {fmtM(montantEff)} {DEVISE_SYMBOLS[projet?.devise]} · Estimatif : {fmtM(projet?.montantTTC)} {DEVISE_SYMBOLS[projet?.devise]}.
                Vérifiez la cohérence avant d'exporter le contrat.
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ DOCUMENT ══════════════ */}
        <div style={{ background: WHITE, borderRadius: 14, boxShadow: "0 4px 24px rgba(0,0,0,0.1)", overflow: "hidden", maxWidth: 840, margin: "0 auto" }}>

          {/* En-tête dark */}
          <div style={{ background: INK, padding: "18px 40px" }}>
            <div style={{ color: GOLD, fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, letterSpacing: 4, textAlign: "center" }}>YOMA DESIGN</div>
            <div style={{ color: "#6A5D52", fontSize: 9, letterSpacing: "0.2em", textAlign: "center", marginTop: 4 }}>ARCHITECTURE INTÉRIEURE · MANDATAIRE DE MAÎTRISE D'OUVRAGE</div>
            <div style={{ height: 1, background: "rgba(201,169,110,0.28)", margin: "12px 0" }} />
            <div style={{ color: WHITE, fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, textAlign: "center" }}>CONTRAT D'EXÉCUTION DES TRAVAUX</div>
            <div style={{ color: GOLD, fontSize: 13, textAlign: "center", marginTop: 4 }}>{projet?.nom}</div>
          </div>

          <div style={{ padding: "28px 40px" }}>

            {/* Parties */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div style={{ background: SOFT, borderRadius: 8, padding: 14, borderLeft: `3px solid ${GOLD}` }}>
                <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 5 }}>MANDATAIRE</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: INK }}>YOMA DESIGN</div>
                <div style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>Youssef Kabdani</div>
                <div style={{ color: MUTED, fontSize: 11, marginTop: 5, fontStyle: "italic" }}>Agissant exclusivement en qualité de mandataire du Maître d'Ouvrage</div>
              </div>
              <div style={{ background: SOFT, borderRadius: 8, padding: 14, borderLeft: `3px solid #5A7B8E` }}>
                <div style={{ color: "#5A7B8E", fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 5 }}>ENTREPRISE</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: INK }}>{entreprise?.nom || "—"}</div>
                <div style={{ color: MUTED, fontSize: 11, marginTop: 2 }}>RC n° {entreprise?.rc} · ICE : {entreprise?.ice}</div>
                <div style={{ color: MUTED, fontSize: 11 }}>{entreprise?.siege}</div>
                <div style={{ color: MUTED, fontSize: 11, fontWeight: 600, marginTop: 2 }}>{entreprise?.representant}{entreprise?.cin && ` · CIN ${entreprise.cin}`}</div>
              </div>
            </div>

            {/* MO banner */}
            <div style={{ background: INK, borderRadius: 8, padding: "10px 16px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 24 }}>
              <span style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", flexShrink: 0 }}>MAÎTRE D'OUVRAGE</span>
              <span style={{ color: WHITE, fontWeight: 600 }}>{projet?.client}</span>
              <span style={{ color: "#6A5D52", fontSize: 12 }}>— {projet?.lot} · {projet?.tf}</span>
              <span style={{ color: "#6A5D52", fontSize: 12 }}>Terrain {projet?.surfaceTerrain} m² · Villa {projet?.surfaceVilla} m²</span>
            </div>

            {/* ── Art. 1 ── */}
            <div className="art">
              <div className="atit">ARTICLE 1 — OBJET</div>
              <div className="abod">
                Le présent contrat a pour objet l'exécution des travaux relatifs au projet de <strong>{projet?.client}</strong>, situé à <strong>{projet?.lot}</strong>.
                <br /><br /><strong>Titre Foncier :</strong> {projet?.tf} · <strong>Terrain :</strong> {projet?.surfaceTerrain} m² · <strong>Villa :</strong> environ {projet?.surfaceVilla} m², tel que décrit en Annexe A.
                <br /><br />YOMA DESIGN intervient exclusivement en qualité de mandataire du Maître d'Ouvrage pour la coordination administrative et la gestion financière du projet.
              </div>
            </div>

            {/* ── Art. 2 ── */}
            <div className="art">
              <div className="atit">ARTICLE 2 — QUALIFICATION DES PARTIES</div>
              <div className="abod">
                L'Entreprise reconnaît expressément que :
                {["YOMA DESIGN agit au nom et pour le compte du Maître d'Ouvrage ;","YOMA DESIGN n'est ni maître d'ouvrage, ni entrepreneur général ;","YOMA DESIGN n'exécute aucun travail matériel ;","YOMA DESIGN n'assume aucune responsabilité technique d'exécution."].map((t, i) => (
                  <div key={i} className="blt"><span className="blt-d">•</span><span>{t}</span></div>
                ))}
                <br />L'Entreprise contracte en toute indépendance juridique, technique, économique et fiscale.
              </div>
            </div>

            {/* ── Art. 3 ── */}
            <div className="art">
              <div className="atit">ARTICLE 3 — RESPONSABILITÉ TECHNIQUE EXCLUSIVE</div>
              <div className="abod">
                L'Entreprise est seule responsable de l'exécution des travaux, leur conformité aux normes, la qualité, les malfaçons, la sécurité du chantier, ses salariés/sous-traitants, et les garanties légales.<br /><br />
                Aucune solidarité contractuelle, technique ou financière ne pourra être recherchée à l'encontre de YOMA DESIGN.
              </div>
            </div>

            {/* ── Art. 4 ── */}
            <div className="art">
              <div className="atit">ARTICLE 4 — ASSURANCES</div>
              <div className="abod">L'Entreprise fournira avant tout démarrage une attestation d'assurance RC professionnelle valide et souscrira toute assurance spécifique requise (décennale le cas échéant).</div>
            </div>

            {/* ── Art. 5 — MONTANT ── */}
            <div className="art">
              <div className="atit">ARTICLE 5 — MONTANT DU MARCHÉ / MODALITÉS DE PAIEMENT</div>
              <div className="abod">
                <div className="mbox">
                  <div style={{ color: MUTED, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 6 }}>MONTANT GLOBAL ET FORFAITAIRE</div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: INK }}>{fmtM(montantEff)} <span style={{ fontSize: 20 }}>{DEVISE_SYMBOLS[projet?.devise] || projet?.devise}</span> TTC</div>
                  <div style={{ color: MUTED, fontStyle: "italic", marginTop: 6, fontSize: 11 }}>({lettres})</div>
                  {projet?.devise === "EUR" && <div style={{ color: MUTED, fontSize: 11, marginTop: 4 }}>Équivalent en dirhams au taux de change du jour du paiement</div>}
                  {montantAlert && <div style={{ color: RED, fontSize: 11, fontWeight: 600, marginTop: 6 }}>⚠️ Montant modifié — à vérifier avec l'Estimatif</div>}
                </div>
                Les paiements seront effectués par YOMA DESIGN en qualité de mandataire, après validation écrite d'un constat d'avancement et sur présentation de justificatifs documentés.
                <br /><br /><strong>Échéancier :</strong>
                {[["30%", "au démarrage effectif du chantier"], ["20%", "à l'achèvement des fondations et première dalle"], ["20%", "à l'achèvement complet du gros œuvre"], ["20%", "en cours de second œuvre"], ["10%", "à la réception finale"]].map(([pct, desc], i) => (
                  <div key={i} className="ech-row">
                    <span style={{ fontWeight: 700, color: GOLD, minWidth: 30 }}>{i + 1}.</span>
                    <span style={{ flex: 1 }}>{pct} — {desc}</span>
                    <span style={{ fontWeight: 700 }}>{fmtM(montantEff * parseFloat(pct) / 100)} {DEVISE_SYMBOLS[projet?.devise]}</span>
                  </div>
                ))}
                <br />Aucun paiement n'est exigible sans validation formelle de YOMA DESIGN.
              </div>
            </div>

            {/* ── Art. 6 ── */}
            <div className="art">
              <div className="atit">ARTICLE 6 — INTERDICTION DE CONTACT DIRECT</div>
              <div className="abod">
                L'Entreprise s'interdit de solliciter directement le Maître d'Ouvrage, de négocier directement toute modification financière ou de contourner YOMA DESIGN pour toute demande contractuelle. Tout contournement constitue une faute grave pouvant entraîner résiliation immédiate.
              </div>
            </div>

            {/* ── Art. 7 ── */}
            <div className="art">
              <div className="atit">ARTICLE 7 — RESPECT DU PLANNING</div>
              <div className="abod">
                L'Entreprise respectera le planning annexé. Tout retard injustifié sera signalé immédiatement à YOMA DESIGN.
                <br /><br /><strong>Durée contractuelle :</strong> {projet?.duree}
                {dateStart && <><br /><strong>Date de démarrage prévue :</strong> {fmtDate(dateStart)}</>}
                {!dateStart && <><br />La date de démarrage sera fixée par ordre de service écrit de YOMA DESIGN.</>}
              </div>
            </div>

            {/* ── Art. 8 ── */}
            <div className="art">
              <div className="atit">ARTICLE 8 — PROCÉDURE DE SUBSTITUTION</div>
              <div className="abod">En cas d'abandon, retard grave ou manquement, YOMA DESIGN adressera une mise en demeure de 7 jours, puis pourra résilier et organiser le remplacement. Aucune indemnité ne sera due à l'Entreprise défaillante.</div>
            </div>

            {/* ── Art. 9 ── */}
            <div className="art">
              <div className="atit">ARTICLE 9 — NON-SOLIDARITÉ</div>
              <div className="abod">Aucune solidarité juridique n'existe entre YOMA DESIGN et l'Entreprise. L'Entreprise ne pourra engager la responsabilité de YOMA DESIGN pour tout acte relevant de sa mission d'exécution technique.</div>
            </div>

            {/* ── Art. 10 — VERROUILLÉ ── */}
            <div className="art">
              <div className="atit">ARTICLE 10 — CONFIDENTIALITÉ <span className="lock">🔒 VERROUILLÉE</span></div>
              <div className="abod">
                L'Entreprise s'engage à ne divulguer aucune information relative :
                {["Au montant global du projet ;","Aux conditions contractuelles entre YOMA DESIGN et le Maître d'Ouvrage ;","À l'organisation financière interne et à la structure de coordination de YOMA DESIGN."].map((t, i) => (
                  <div key={i} className="blt"><span className="blt-d">•</span><span>{t}</span></div>
                ))}
                <br />Cette obligation de confidentialité est perpétuelle et survit à l'extinction du contrat.
              </div>
            </div>

            {/* ── Art. 11 ── */}
            <div className="art">
              <div className="atit">ARTICLE 11 — RÉSILIATION</div>
              <div className="abod">En cas de manquement grave, le contrat sera résilié après mise en demeure sans effet. Les travaux réalisés et conformes seront réglés au prorata de leur avancement validé.</div>
            </div>

            {/* ── Art. 12 — VERROUILLÉ ARBITRAGE ── */}
            <div className="art">
              <div className="atit">ARTICLE 12 — DROIT APPLICABLE ET RÈGLEMENT DES LITIGES <span className="lock">🔒 VERROUILLÉE</span></div>
              <div className="abod">
                Le présent contrat est soumis au droit marocain. Tout différend fera l'objet d'une tentative amiable de 15 jours. À défaut, il sera tranché par arbitrage conformément à la loi marocaine n° 95-17.
                <div className="abox" style={{ marginTop: 10 }}>
                  <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 5 }}>ARBITRE UNIQUE DÉSIGNÉ D'UN COMMUN ACCORD</div>
                  <div style={{ color: WHITE, fontWeight: 700, fontSize: 15 }}>Monsieur Abderrahim ABOUELOUAFA</div>
                  <div style={{ color: "#8A7B6D", fontSize: 12, marginTop: 3 }}>Siège de l'arbitrage : Marrakech · Langue : français</div>
                </div>
              </div>
            </div>

            {/* ── Art. 13 ── */}
            <div className="art">
              <div className="atit">ARTICLE 13 — RÉCEPTION DES TRAVAUX</div>
              <div className="abod">La réception interviendra en présence du Maître d'Ouvrage, de YOMA DESIGN et de l'Entreprise. Un procès-verbal sera établi. La réception déclenche la garantie de parfait achèvement.</div>
            </div>

            {/* Signatures */}
            <div style={{ marginTop: 32, paddingTop: 18, borderTop: `2px solid ${BORDER}` }}>
              <div style={{ textAlign: "center", color: MUTED, fontSize: 12, marginBottom: 22 }}>
                Fait en deux (2) exemplaires originaux — À Marrakech, le {fmtDate(dateSign)}
                {dateStart && <> — Démarrage prévu le {fmtDate(dateStart)}</>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                <div className="sig">
                  <div style={{ color: MUTED, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 6 }}>L'ENTREPRISE</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: INK }}>{entreprise?.nom}</div>
                  <div style={{ color: MUTED, fontSize: 11 }}>{entreprise?.representant}</div>
                  <div style={{ color: MUTED, fontSize: 11 }}>RC n° {entreprise?.rc}</div>
                  <div style={{ marginTop: 16, height: 64, border: `1px dashed ${BORDER}`, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: BORDER, fontSize: 11 }}>Cachet + Signature + « Bon pour accord »</span>
                  </div>
                </div>
                <div className="sig">
                  <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: ".1em", marginBottom: 6 }}>YOMA DESIGN</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: INK }}>Youssef Kabdani</div>
                  <div style={{ color: MUTED, fontSize: 11 }}>Mandataire du Maître d'Ouvrage</div>
                  <div style={{ marginTop: 16, height: 64, border: `1px dashed ${BORDER}`, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ color: BORDER, fontSize: 11 }}>Cachet et Signature YOMA DESIGN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ════════ MODAL PROJET ════════ */}
      {showProjMod && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: WHITE, borderRadius: 14, padding: 28, width: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700 }}>Nouveau Projet</div>
              <button onClick={() => setShowProjMod(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: MUTED }}>✕</button>
            </div>
            {[["Nom du projet","nom","text","ex: PROJET MARTIN – VILLA"],["Maître d'Ouvrage","client","text","ex: Madame Charlotte CACI"],["Lot / Adresse","lot","text","ex: Lot 156 — Tranche 10"],["Titre Foncier","tf","text","ex: TF 36565/65"],["Superficie terrain (m²)","surfaceTerrain","number","383"],["Superficie villa (m²)","surfaceVilla","number","215"],["Ville","ville","text","Marrakech"],["Durée des travaux","duree","text","ex: 12 à 15 mois"]].map(([label, key, type, ph]) => (
              <div key={key} style={{ marginBottom: 11 }}>
                <label style={lbl}>{label.toUpperCase()}</label>
                <input type={type} placeholder={ph} value={newProj[key] || ""} onChange={e => setNewProj(p => ({ ...p, [key]: e.target.value }))} className="cinp" />
              </div>
            ))}
            <div style={{ marginBottom: 11 }}>
              <label style={lbl}>MONTANT TTC</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="number" placeholder="110000" value={newProj.montantTTC || ""} onChange={e => setNewProj(p => ({ ...p, montantTTC: parseFloat(e.target.value) }))} className="cinp" style={{ flex: 2 }} />
                <select value={newProj.devise} onChange={e => setNewProj(p => ({ ...p, devise: e.target.value }))} className="csel" style={{ flex: 1 }}>
                  {["MAD","EUR","USD","GBP","CHF"].map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button className="cbtn" onClick={() => setShowProjMod(false)} style={{ flex: 1, background: SOFT, color: MUTED }}>Annuler</button>
              <button className="cbtn" onClick={saveProj} style={{ flex: 2, background: GOLD, color: INK }}>✓ Enregistrer le projet</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ MODAL ENTREPRISE ════════ */}
      {showCompMod && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: WHITE, borderRadius: 14, padding: 28, width: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700 }}>Nouvelle Entreprise</div>
              <button onClick={() => setShowCompMod(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: MUTED }}>✕</button>
            </div>
            {[["Raison sociale","nom","ex: CM KABDANI CONSTRUCTION SARLAU"],["N° RC","rc","ex: 166873"],["ICE","ice","ex: 003751270000006"],["Siège social","siege","ex: Douar Chouiter – Marrakech"],["Représentant légal","representant","ex: KABDANI MUSTAPHA"],["N° CIN","cin","ex: F824320"]].map(([label, key, ph]) => (
              <div key={key} style={{ marginBottom: 11 }}>
                <label style={lbl}>{label.toUpperCase()}</label>
                <input type="text" placeholder={ph} value={newComp[key] || ""} onChange={e => setNewComp(c => ({ ...c, [key]: e.target.value }))} className="cinp" />
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button className="cbtn" onClick={() => setShowCompMod(false)} style={{ flex: 1, background: SOFT, color: MUTED }}>Annuler</button>
              <button className="cbtn" onClick={saveComp} style={{ flex: 2, background: GOLD, color: INK }}>✓ Enregistrer l'entreprise</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
